import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Assessment,
  Badge,
  Business,
  Domain,
  Groups,
  Insights,
  Layers,
  Link,
  LockPerson,
  People,
  Person,
  Refresh,
  Storefront,
  SupervisorAccount,
  ToggleOff,
  ToggleOn,
  VerifiedUser,
} from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import { MRT_Localization_ES } from 'material-react-table/locales/es';

import { SystemModulesEnum } from '@/auth/interfaces/system-modules.enum';
import { useCheckSystemModules } from '@/shared/hooks';
import { useFetchReporteUsuariosCompleto } from '../actions/reporte-usuarios.actions';
import { useFetchReporteUsuariosSoftnet } from '../actions/reporte-usuarios-softnet.actions';
import {
  ReporteUsuariosAreaRow,
  ReporteUsuariosCanalVentaRow,
  ReporteUsuariosDepartamentoRow,
  ReporteUsuariosEmpresaRow,
  ReporteUsuariosGroupPermPair,
  ReporteUsuariosGrupoRow,
  ReporteUsuariosModuloRow,
  ReporteUsuariosPermisoRow,
  ReporteUsuariosUserGroupPair,
  ReporteUsuariosUserPermPair,
  ReporteUsuariosUserRow,
} from '../interfaces/reporte-usuarios.interface';
import {
  ReporteUsuariosSoftnetCargoRow,
  ReporteUsuariosSoftnetDepartamentoRow,
  ReporteUsuariosSoftnetModuloRow,
  ReporteUsuariosSoftnetUserRow,
  UserCargoPair,
  UserDeptoPair,
} from '../interfaces/reporte-usuarios-softnet.interface';

const CHART_COLORS = [
  '#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#0288d1', '#c2185b',
  '#00796b', '#fbc02d', '#5d4037', '#455a64', '#e64a19', '#303f9f',
];

interface KpiCardProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color?: string;
  hint?: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, icon, color = 'primary.main', hint }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography color="text.secondary" variant="body2" gutterBottom>
            {label}
          </Typography>
          <Typography variant="h4" fontWeight="bold">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </Typography>
          {hint && (
            <Typography variant="caption" color="text.secondary">
              {hint}
            </Typography>
          )}
        </Box>
        <Box sx={{ color }}>{icon}</Box>
      </Stack>
    </CardContent>
  </Card>
);

const renderChips = (items?: string[] | null, max = 4) => {
  if (!items || items.length === 0) return <Chip size="small" label="—" variant="outlined" />;
  const visible = items.slice(0, max);
  const extra = items.length - visible.length;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {visible.map((it) => (
        <Chip key={it} size="small" label={it} />
      ))}
      {extra > 0 && <Chip size="small" label={`+${extra}`} variant="outlined" />}
    </Stack>
  );
};

const TabPanel: React.FC<{ value: number; index: number; children: React.ReactNode }> = ({
  value,
  index,
  children,
}) => (value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null);

const ReporteUsuariosDashboardPage: React.FC = () => {
  useCheckSystemModules(SystemModulesEnum.USUARIOS_REPORTE_USUARIOS);

  const [mainTab, setMainTab] = useState(0);
  const [evaSubTab, setEvaSubTab] = useState(0);
  const [softnetSubTab, setSoftnetSubTab] = useState(0);
  const { data: res, isLoading, isError, refetch, isRefetching } =
    useFetchReporteUsuariosCompleto();
  const {
    data: softnetRes,
    isLoading: softnetLoading,
    isError: softnetError,
    refetch: softnetRefetch,
    isRefetching: softnetRefetching,
  } = useFetchReporteUsuariosSoftnet();

  const data = res?.data;
  const softnetData = softnetRes?.data;

  // ===== Columnas Usuarios ===============================================
  const usuariosColumns = useMemo<MRT_ColumnDef<ReporteUsuariosUserRow>[]>(
    () => [
      { accessorKey: 'username', header: 'Usuario', size: 140 },
      {
        header: 'Nombre completo',
        size: 240,
        accessorFn: (r) =>
          [r.nombre, r.apellido].filter(Boolean).join(' ') ||
          r.razon_social ||
          [r.first_name, r.last_name].filter(Boolean).join(' '),
      },
      { accessorKey: 'identificacion', header: 'Identificación', size: 130 },
      { accessorKey: 'email', header: 'Email', size: 220 },
      { accessorKey: 'rol', header: 'Rol', size: 140 },
      { accessorKey: 'empresa_nombre', header: 'Empresa', size: 180 },
      { accessorKey: 'area_nombre', header: 'Área', size: 160 },
      { accessorKey: 'departamento_nombre', header: 'Departamento', size: 180 },
      { accessorKey: 'canal_venta_nombre', header: 'Canal de venta', size: 180 },
      {
        header: 'Estado',
        size: 100,
        accessorFn: (r) => (r.is_active ? 'Activo' : 'Inactivo'),
        Cell: ({ row }) => (
          <Chip
            size="small"
            label={row.original.is_active ? 'Activo' : 'Inactivo'}
            color={row.original.is_active ? 'success' : 'default'}
            variant={row.original.is_active ? 'filled' : 'outlined'}
          />
        ),
      },
      {
        header: 'Conectado',
        size: 110,
        accessorFn: (r) => (r.en_linea ? 'En línea' : 'Offline'),
        Cell: ({ row }) =>
          row.original.en_linea ? (
            <Chip size="small" color="success" icon={<ToggleOn />} label="En línea" />
          ) : (
            <Chip size="small" variant="outlined" icon={<ToggleOff />} label="Offline" />
          ),
      },
      {
        header: 'Grupos',
        size: 240,
        accessorFn: (r) => r.grupos.join(', '),
        Cell: ({ row }) => renderChips(row.original.grupos, 3),
      },
      {
        header: 'Módulos',
        size: 70,
        accessorKey: 'total_modulos',
      },
      {
        header: 'Permisos',
        size: 80,
        accessorKey: 'total_permisos_efectivos',
      },
      {
        accessorKey: 'is_superuser',
        header: 'Superuser',
        size: 100,
        Cell: ({ row }) => (row.original.is_superuser ? '✅' : '—'),
      },
      {
        accessorKey: 'date_joined',
        header: 'Creado',
        size: 130,
        Cell: ({ row }) =>
          row.original.date_joined
            ? new Date(row.original.date_joined).toLocaleDateString()
            : '—',
      },
      {
        accessorKey: 'last_login',
        header: 'Último login',
        size: 150,
        Cell: ({ row }) =>
          row.original.last_login
            ? new Date(row.original.last_login).toLocaleString()
            : '—',
      },
    ],
    [],
  );

  const areasColumns = useMemo<MRT_ColumnDef<ReporteUsuariosAreaRow>[]>(
    () => [
      { accessorKey: 'codigo', header: 'Código', size: 120 },
      { accessorKey: 'nombre', header: 'Área', size: 280 },
      { accessorKey: 'empresa_nombre', header: 'Empresa', size: 240 },
    ],
    [],
  );

  const departamentosColumns = useMemo<MRT_ColumnDef<ReporteUsuariosDepartamentoRow>[]>(
    () => [
      { accessorKey: 'codigo', header: 'Código', size: 120 },
      { accessorKey: 'nombre', header: 'Departamento', size: 240 },
      { accessorKey: 'area_nombre', header: 'Área', size: 200 },
      { accessorKey: 'empresa_nombre', header: 'Empresa', size: 220 },
    ],
    [],
  );

  const canalesColumns = useMemo<MRT_ColumnDef<ReporteUsuariosCanalVentaRow>[]>(
    () => [
      { accessorKey: 'codigo', header: 'Código', size: 120 },
      { accessorKey: 'nombre', header: 'Canal de venta', size: 280 },
      { accessorKey: 'supervisor_id', header: 'Supervisor (código)', size: 180 },
    ],
    [],
  );

  const empresasColumns = useMemo<MRT_ColumnDef<ReporteUsuariosEmpresaRow>[]>(
    () => [
      { accessorKey: 'codigo', header: 'Código', size: 100 },
      { accessorKey: 'razon_social', header: 'Razón Social', size: 260 },
      { accessorKey: 'nombre_comercial', header: 'Nombre Comercial', size: 220 },
      { accessorKey: 'identificacion', header: 'Identificación', size: 140 },
      { accessorKey: 'email', header: 'Email', size: 220 },
      { accessorKey: 'telefono_1', header: 'Teléfono', size: 140 },
    ],
    [],
  );

  const gruposColumns = useMemo<MRT_ColumnDef<ReporteUsuariosGrupoRow>[]>(
    () => [
      { accessorKey: 'name', header: 'Grupo', size: 260 },
      { accessorKey: 'total_usuarios', header: 'Usuarios', size: 100 },
      { accessorKey: 'total_modulos', header: 'Módulos', size: 100 },
      { accessorKey: 'total_permisos', header: 'Permisos', size: 100 },
      {
        header: 'Módulos asignados',
        size: 320,
        accessorFn: (g) => g.modulos.join(', '),
        Cell: ({ row }) => renderChips(row.original.modulos, 4),
      },
    ],
    [],
  );

  const modulosColumns = useMemo<MRT_ColumnDef<ReporteUsuariosModuloRow>[]>(
    () => [
      { accessorKey: 'codigo', header: 'Código', size: 360 },
      { accessorKey: 'grupos_que_lo_usan', header: 'Grupos que lo usan', size: 160 },
      {
        accessorKey: 'registrado',
        header: 'Registrado',
        size: 130,
        Cell: ({ row }) =>
          row.original.registrado ? (
            <Chip size="small" label="Sí" color="success" />
          ) : (
            <Chip size="small" label="No (legacy)" color="warning" variant="outlined" />
          ),
      },
    ],
    [],
  );

  const permisosColumns = useMemo<MRT_ColumnDef<ReporteUsuariosPermisoRow>[]>(
    () => [
      { accessorKey: 'codigo', header: 'Código (app.codename)', size: 280 },
      { accessorKey: 'name', header: 'Descripción', size: 320 },
      { accessorKey: 'app_label', header: 'App', size: 100 },
      { accessorKey: 'model', header: 'Modelo', size: 160 },
    ],
    [],
  );

  const userGroupsColumns = useMemo<MRT_ColumnDef<ReporteUsuariosUserGroupPair>[]>(
    () => [
      { accessorKey: 'user_id', header: 'User ID', size: 100 },
      { accessorKey: 'group_id', header: 'Group ID', size: 100 },
      { accessorKey: 'group_name', header: 'Grupo', size: 280 },
    ],
    [],
  );

  const userPermsColumns = useMemo<MRT_ColumnDef<ReporteUsuariosUserPermPair>[]>(
    () => [
      { accessorKey: 'user_id', header: 'User ID', size: 100 },
      { accessorKey: 'permission_id', header: 'Permiso ID', size: 110 },
      { accessorKey: 'permiso_codigo', header: 'Permiso (código)', size: 320 },
    ],
    [],
  );

  const groupPermsColumns = useMemo<MRT_ColumnDef<ReporteUsuariosGroupPermPair>[]>(
    () => [
      { accessorKey: 'group_id', header: 'Group ID', size: 100 },
      { accessorKey: 'permission_id', header: 'Permiso ID', size: 110 },
      { accessorKey: 'permiso_codigo', header: 'Permiso (código)', size: 320 },
    ],
    [],
  );

  // ===== Columnas SOFTNET ================================================
  const softnetUsuariosColumns = useMemo<MRT_ColumnDef<ReporteUsuariosSoftnetUserRow>[]>(
    () => [
      { accessorKey: 'nick', header: 'Usuario', size: 140 },
      { accessorKey: 'nombre', header: 'Nombre completo', size: 300 },
      { accessorKey: 'identificacion', header: 'Identificación', size: 140 },
      { accessorKey: 'email', header: 'Email', size: 260 },
      { accessorKey: 'area', header: 'Área', size: 160 },
      { accessorKey: 'perfil', header: 'Perfil', size: 220 },
      { accessorKey: 'departamento', header: 'Departamento', size: 160 },
      {
        id: 'estado',
        header: 'Estado',
        size: 100,
        accessorFn: (r) => r.estado,
        Cell: ({ row }) => (
          <Chip
            size="small"
            label={row.original.estado === 'activo' ? 'Activo' : 'Inactivo'}
            color={row.original.estado === 'activo' ? 'success' : 'default'}
            variant={row.original.estado === 'activo' ? 'filled' : 'outlined'}
          />
        ),
      },
      {
        accessorKey: 'fecha_registro',
        header: 'Fecha registro',
        size: 150,
        Cell: ({ row }) =>
          row.original.fecha_registro
            ? new Date(row.original.fecha_registro).toLocaleString()
            : '—',
      },
      {
        accessorKey: 'ultima_sesion',
        header: 'Último login',
        size: 150,
        Cell: ({ row }) =>
          row.original.ultima_sesion
            ? new Date(row.original.ultima_sesion).toLocaleString()
            : '—',
      },
    ],
    [],
  );

  // ===== Columnas Módulos SOFTNET =========================================
  const softnetModulosColumns = useMemo<MRT_ColumnDef<ReporteUsuariosSoftnetModuloRow>[]>(
    () => [
      { accessorKey: 'nomVista', header: 'Nombre de vista', size: 280 },
      { accessorKey: 'descripcion', header: 'Descripción', size: 240 },
      { accessorKey: 'tipo', header: 'Tipo', size: 100 },
      { accessorKey: 'path', header: 'Path', size: 200 },
      {
        accessorKey: 'estado',
        header: 'Estado',
        size: 100,
        Cell: ({ row }) => (
          <Chip
            size="small"
            label={row.original.estado === 'A' ? 'Activo' : 'Inactivo'}
            color={row.original.estado === 'A' ? 'success' : 'default'}
            variant={row.original.estado === 'A' ? 'filled' : 'outlined'}
          />
        ),
      },
    ],
    [],
  );

  // ===== Columnas Departamentos SOFTNET ===================================
  const softnetDeptosColumns = useMemo<MRT_ColumnDef<ReporteUsuariosSoftnetDepartamentoRow>[]>(
    () => [
      { accessorKey: 'id_Departamento', header: 'ID', size: 80 },
      { accessorKey: 'departamento', header: 'Departamento', size: 280 },
      {
        accessorKey: 'estado',
        header: 'Estado',
        size: 100,
        Cell: ({ row }) => (
          <Chip
            size="small"
            label={row.original.estado === '1' ? 'Activo' : 'Inactivo'}
            color={row.original.estado === '1' ? 'success' : 'default'}
            variant={row.original.estado === '1' ? 'filled' : 'outlined'}
          />
        ),
      },
    ],
    [],
  );

  // ===== Columnas Cargos SOFTNET ==========================================
  const softnetCargosColumns = useMemo<MRT_ColumnDef<ReporteUsuariosSoftnetCargoRow>[]>(
    () => [
      { accessorKey: 'cargo', header: 'Cargo', size: 160 },
      { accessorKey: 'descripcion', header: 'Descripción', size: 280 },
      { accessorKey: 'path_inicio', header: 'Ruta inicio', size: 200 },
      {
        header: 'Horario',
        size: 160,
        accessorFn: (r) => `${r.hora_inicio} - ${r.hora_fin}`,
      },
    ],
    [],
  );

  const userCargoColumns = useMemo<MRT_ColumnDef<UserCargoPair>[]>(
    () => [
      { accessorKey: 'nick', header: 'Usuario', size: 140 },
      { accessorKey: 'nombre', header: 'Nombre', size: 300 },
      { accessorKey: 'perfil', header: 'Perfil asignado', size: 240 },
      { accessorKey: 'cargo_nombre', header: 'Cargo', size: 160 },
      { accessorKey: 'cargo_descripcion', header: 'Descripción cargo', size: 260 },
    ],
    [],
  );

  const userDeptoColumns = useMemo<MRT_ColumnDef<UserDeptoPair>[]>(
    () => [
      { accessorKey: 'nick', header: 'Usuario', size: 140 },
      { accessorKey: 'nombre', header: 'Nombre', size: 300 },
      { accessorKey: 'departamento', header: 'Departamento', size: 240 },
    ],
    [],
  );

  // ===== Hooks de tabla por sección ======================================
  const tableProps = {
    enableColumnFilters: true,
    enableColumnFilterModes: true,
    enableGlobalFilter: true,
    enableDensityToggle: true,
    enableFullScreenToggle: true,
    enableColumnOrdering: true,
    enableColumnPinning: true,
    initialState: {
      density: 'compact' as const,
      pagination: { pageSize: 25, pageIndex: 0 },
    },
    localization: MRT_Localization_ES,
    muiTableContainerProps: { sx: { maxHeight: 600 } },
  };

  const usuariosTable = useMaterialReactTable({
    ...tableProps,
    columns: usuariosColumns,
    data: data?.usuarios ?? [],
    state: { isLoading, showProgressBars: isRefetching },
  });

  const areasTable = useMaterialReactTable({
    ...tableProps,
    columns: areasColumns,
    data: data?.areas ?? [],
    state: { isLoading, showProgressBars: isRefetching },
  });

  const departamentosTable = useMaterialReactTable({
    ...tableProps,
    columns: departamentosColumns,
    data: data?.departamentos ?? [],
    state: { isLoading, showProgressBars: isRefetching },
  });

  const canalesTable = useMaterialReactTable({
    ...tableProps,
    columns: canalesColumns,
    data: data?.canales_venta ?? [],
    state: { isLoading, showProgressBars: isRefetching },
  });

  const empresasTable = useMaterialReactTable({
    ...tableProps,
    columns: empresasColumns,
    data: data?.empresas ?? [],
    state: { isLoading, showProgressBars: isRefetching },
  });

  const gruposTable = useMaterialReactTable({
    ...tableProps,
    columns: gruposColumns,
    data: data?.grupos ?? [],
    state: { isLoading, showProgressBars: isRefetching },
  });

  const modulosTable = useMaterialReactTable({
    ...tableProps,
    columns: modulosColumns,
    data: data?.modulos ?? [],
    state: { isLoading, showProgressBars: isRefetching },
  });

  const permisosTable = useMaterialReactTable({
    ...tableProps,
    columns: permisosColumns,
    data: data?.permisos ?? [],
    state: { isLoading, showProgressBars: isRefetching },
  });

  const userGroupsTable = useMaterialReactTable({
    ...tableProps,
    columns: userGroupsColumns,
    data: data?.usuario_grupos ?? [],
    state: { isLoading, showProgressBars: isRefetching },
  });

  const userPermsTable = useMaterialReactTable({
    ...tableProps,
    columns: userPermsColumns,
    data: data?.usuario_permisos ?? [],
    state: { isLoading, showProgressBars: isRefetching },
  });

  const groupPermsTable = useMaterialReactTable({
    ...tableProps,
    columns: groupPermsColumns,
    data: data?.grupo_permisos ?? [],
    state: { isLoading, showProgressBars: isRefetching },
  });

  const softnetUsuariosTable = useMaterialReactTable({
    ...tableProps,
    columns: softnetUsuariosColumns,
    data: softnetData?.usuarios ?? [],
    state: { isLoading: softnetLoading, showProgressBars: softnetRefetching },
    enableColumnOrdering: false,
    initialState: {
      ...tableProps.initialState,
      columnOrder: [
        'nick',
        'nombre',
        'identificacion',
        'email',
        'area',
        'perfil',
        'departamento',
        'estado',
        'fecha_registro',
        'ultima_sesion',
      ],
    },
  });

  const softnetModulosTable = useMaterialReactTable({
    ...tableProps,
    columns: softnetModulosColumns,
    data: softnetData?.modulos ?? [],
    state: { isLoading: softnetLoading, showProgressBars: softnetRefetching },
    enableColumnOrdering: false,
  });

  const softnetDeptosTable = useMaterialReactTable({
    ...tableProps,
    columns: softnetDeptosColumns,
    data: softnetData?.departamentos ?? [],
    state: { isLoading: softnetLoading, showProgressBars: softnetRefetching },
    enableColumnOrdering: false,
  });

  const softnetCargosTable = useMaterialReactTable({
    ...tableProps,
    columns: softnetCargosColumns,
    data: softnetData?.cargos ?? [],
    state: { isLoading: softnetLoading, showProgressBars: softnetRefetching },
    enableColumnOrdering: false,
  });

  const softnetUserCargoPairs = useMemo(() => {
    if (!softnetData?.usuarios || !softnetData?.cargos) return [];
    return softnetData.usuarios
      .filter((u) => u.perfil)
      .map((u) => {
        const cargo = softnetData.cargos?.find((c) => u.perfil.includes(c.descripcion) || c.descripcion.includes(u.perfil));
        return {
          nick: u.nick,
          nombre: u.nombre,
          perfil: u.perfil,
          cargo_id: cargo?.id_cargo ?? '—',
          cargo_nombre: cargo?.cargo ?? '—',
          cargo_descripcion: cargo?.descripcion ?? '—',
        };
      });
  }, [softnetData]);

  const softnetUserDeptoPairs = useMemo(() => {
    if (!softnetData?.usuarios) return [];
    return softnetData.usuarios
      .filter((u) => u.departamento)
      .map((u) => ({
        nick: u.nick,
        nombre: u.nombre,
        departamento: u.departamento,
      }));
  }, [softnetData]);

  const userCargoTable = useMaterialReactTable({
    ...tableProps,
    columns: userCargoColumns,
    data: softnetUserCargoPairs,
    state: { isLoading: softnetLoading, showProgressBars: softnetRefetching },
    enableColumnOrdering: false,
  });

  const userDeptoTable = useMaterialReactTable({
    ...tableProps,
    columns: userDeptoColumns,
    data: softnetUserDeptoPairs,
    state: { isLoading: softnetLoading, showProgressBars: softnetRefetching },
    enableColumnOrdering: false,
  });

  const softnetUsersByDept = useMemo(() => {
    if (!softnetData?.usuarios) return [];
    const map = new Map<string, number>();
    softnetData.usuarios.forEach((u) => {
      const key = u.departamento || 'Sin departamento';
      map.set(key, (map.get(key) ?? 0) + 1);
    });
    return Array.from(map.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);
  }, [softnetData]);

  const softnetUsersByProfile = useMemo(() => {
    if (!softnetData?.usuarios) return [];
    const map = new Map<string, number>();
    softnetData.usuarios.forEach((u) => {
      const key = u.perfil || 'Sin perfil';
      map.set(key, (map.get(key) ?? 0) + 1);
    });
    return Array.from(map.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);
  }, [softnetData]);

  const softnetModulosByType = useMemo(() => {
    if (!softnetData?.modulos) return [];
    const map = new Map<string, number>();
    softnetData.modulos.forEach((m) => {
      map.set(m.tipo, (map.get(m.tipo) ?? 0) + 1);
    });
    return Array.from(map.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);
  }, [softnetData]);

  const softnetUsersByArea = useMemo(() => {
    if (!softnetData?.usuarios) return [];
    const map = new Map<string, number>();
    softnetData.usuarios.forEach((u) => {
      const key = u.area || 'Sin área';
      map.set(key, (map.get(key) ?? 0) + 1);
    });
    return Array.from(map.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);
  }, [softnetData]);

  const softnetUserEstadoPie = useMemo(() => {
    if (!softnetData?.usuarios) return [];
    const activos = softnetData.usuarios.filter((u) => u.estado === 'activo').length;
    const inactivos = softnetData.usuarios.length - activos;
    return [
      { label: 'Activos', value: activos },
      { label: 'Inactivos', value: inactivos },
    ];
  }, [softnetData]);

  return (
    <Box sx={{ p: 2 }}>
      {/* Header ================================================== */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
            Reporte de Usuarios del Sistema
          </Typography>
          {mainTab === 0 && data && (
            <Typography variant="caption" color="text.secondary">
              Generado: {new Date(data.generado_en).toLocaleString()}
            </Typography>
          )}
          {mainTab === 1 && (
            <Typography variant="caption" color="text.secondary">
              Reporte de usuarios SOFTNET
            </Typography>
          )}
        </Box>
        <Tooltip title="Refrescar">
          <span>
            <IconButton
              onClick={() => (mainTab === 0 ? refetch() : softnetRefetch())}
              disabled={mainTab === 0 ? isRefetching : softnetRefetching}
            >
              {mainTab === 0 && isRefetching ? <CircularProgress size={22} /> : null}
              {mainTab === 1 && softnetRefetching ? <CircularProgress size={22} /> : null}
              {mainTab === 0 && !isRefetching ? <Refresh /> : null}
              {mainTab === 1 && !softnetRefetching ? <Refresh /> : null}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {/* Main Tabs: EVA | SOFTNET ================================= */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={mainTab}
          onChange={(_, v) => setMainTab(v)}
          variant="fullWidth"
        >
          <Tab label="EVA" />
          <Tab label="SOFTNET" />
        </Tabs>
      </Paper>

      {/* ============ EVA SECTION ================================= */}
      {mainTab === 0 && (
        <>
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
              <CircularProgress />
            </Box>
          ) : isError || !data ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="error">
                Error al cargar el reporte de usuarios. Verifique permisos y disponibilidad del backend.
              </Alert>
            </Box>
          ) : (
            <>
              <Paper sx={{ mb: 2 }}>
                <Tabs
                  value={evaSubTab}
                  onChange={(_, v) => setEvaSubTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab icon={<Insights />} iconPosition="start" label="Dashboard" />
                  <Tab icon={<People />} iconPosition="start" label={`Usuarios (${data.usuarios.length})`} />
                  <Tab icon={<Groups />} iconPosition="start" label={`Grupos (${data.grupos.length})`} />
                  <Tab icon={<Layers />} iconPosition="start" label={`Módulos (${data.modulos.length})`} />
                  <Tab icon={<LockPerson />} iconPosition="start" label={`Permisos (${data.permisos.length})`} />
                  <Tab icon={<Business />} iconPosition="start" label={`Empresas (${data.empresas.length})`} />
                  <Tab icon={<Domain />} iconPosition="start" label={`Áreas (${data.areas.length})`} />
                  <Tab icon={<Domain />} iconPosition="start" label={`Departamentos (${data.departamentos.length})`} />
                  <Tab icon={<Storefront />} iconPosition="start" label={`Canales (${data.canales_venta.length})`} />
                  <Tab icon={<Link />} iconPosition="start" label={`Usuario→Grupos (${data.usuario_grupos.length})`} />
                  <Tab icon={<Link />} iconPosition="start" label={`Usuario→Permisos (${data.usuario_permisos.length})`} />
                  <Tab icon={<Link />} iconPosition="start" label={`Grupo→Permisos (${data.grupo_permisos.length})`} />
                </Tabs>
              </Paper>

              <TabPanel value={evaSubTab} index={0}>
                {(() => {
                  const k = data.kpis.totales;
                  return (
                    <>
                      <Grid container spacing={2} mb={2}>
                        <Grid item xs={12} sm={6} md={3}>
                          <KpiCard label="Usuarios" value={k.usuarios} icon={<People sx={{ fontSize: 40 }} />} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <KpiCard
                            label="Activos"
                            value={k.usuarios_activos}
                            hint={`${k.usuarios_inactivos.toLocaleString()} inactivos`}
                            icon={<VerifiedUser sx={{ fontSize: 40 }} />}
                            color="success.main"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <KpiCard
                            label="Superusers"
                            value={k.superusers}
                            icon={<SupervisorAccount sx={{ fontSize: 40 }} />}
                            color="warning.main"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <KpiCard label="Grupos" value={k.grupos} icon={<Groups sx={{ fontSize: 40 }} />} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <KpiCard label="Módulos" value={k.modulos} icon={<Layers sx={{ fontSize: 40 }} />} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <KpiCard label="Permisos" value={k.permisos} icon={<LockPerson sx={{ fontSize: 40 }} />} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <KpiCard
                            label="Sin grupo"
                            value={k.usuarios_sin_grupo}
                            icon={<Person sx={{ fontSize: 40 }} />}
                            color="error.main"
                            hint="Usuarios sin ningún rol asignado"
                          />
                        </Grid>
                      </Grid>

                      <Grid container spacing={2}>
                        {/* Usuarios por área ----- */}
                        <Grid item xs={12} lg={6}>
                          <Paper sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>
                Usuarios por Área
              </Typography>
                            <ResponsiveContainer width="100%" height={320}>
                              <BarChart data={data.kpis.usuarios_por_area}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="label" angle={-30} textAnchor="end" height={90} interval={0} />
                                <YAxis allowDecimals={false} />
                                <ReTooltip />
                                <Bar dataKey="value" fill={CHART_COLORS[0]} name="Usuarios" />
                              </BarChart>
                            </ResponsiveContainer>
                          </Paper>
                        </Grid>

          {/* Usuarios por departamento ----- */}
          <Grid item xs={12} lg={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Usuarios por Departamento
              </Typography>
                            <ResponsiveContainer width="100%" height={320}>
                              <BarChart data={data.kpis.usuarios_por_departamento}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="label" angle={-30} textAnchor="end" height={90} interval={0} />
                                <YAxis allowDecimals={false} />
                                <ReTooltip />
                                <Bar dataKey="value" fill={CHART_COLORS[1]} name="Usuarios" />
                              </BarChart>
                            </ResponsiveContainer>
                          </Paper>
                        </Grid>

          {/* Por canal de venta ----- */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Usuarios por Canal de Venta
              </Typography>
              <ResponsiveContainer
                width="100%"
                height={Math.max(
                  320,
                  data.kpis.usuarios_por_canal_venta.length * 26,
                )}
              >
                <BarChart
                  data={data.kpis.usuarios_por_canal_venta}
                  layout="vertical"
                  margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="label"
                    width={220}
                    tick={{ fontSize: 12 }}
                    interval={0}
                  />
                  <ReTooltip />
                  <Bar dataKey="value" name="Usuarios" radius={[0, 4, 4, 0]}>
                    {data.kpis.usuarios_por_canal_venta.map((_, i) => (
                      <Cell
                        key={i}
                        fill={CHART_COLORS[i % CHART_COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Por rol ----- */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Usuarios por Rol
              </Typography>
              <ResponsiveContainer
                width="100%"
                height={Math.max(
                  320,
                  data.kpis.usuarios_por_rol.length * 26,
                )}
              >
                <BarChart
                  data={data.kpis.usuarios_por_rol}
                  layout="vertical"
                  margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="label"
                    width={220}
                    tick={{ fontSize: 12 }}
                    interval={0}
                  />
                  <ReTooltip />
                  <Bar dataKey="value" name="Usuarios" radius={[0, 4, 4, 0]}>
                    {data.kpis.usuarios_por_rol.map((_, i) => (
                      <Cell
                        key={i}
                        fill={CHART_COLORS[(i + 3) % CHART_COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Grupos top ----- */}
          <Grid item xs={12} lg={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Top Grupos por nº de Usuarios
              </Typography>
              <ResponsiveContainer width="100%" height={360}>
                <BarChart data={data.kpis.grupos_top} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={180} />
                  <ReTooltip />
                  <Bar dataKey="value" fill={CHART_COLORS[2]} name="Usuarios" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Módulos top ----- */}
          <Grid item xs={12} lg={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Módulos más asignados a grupos
              </Typography>
              <ResponsiveContainer width="100%" height={360}>
                <BarChart data={data.kpis.modulos_top} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="label" width={220} />
                  <ReTooltip />
                  <Bar dataKey="value" fill={CHART_COLORS[3]} name="Grupos que lo usan" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

        </Grid>
                    </>
                  );
                })()}
              </TabPanel>

              <TabPanel value={evaSubTab} index={1}>
                <MaterialReactTable table={usuariosTable} />
              </TabPanel>
              <TabPanel value={evaSubTab} index={2}>
                <MaterialReactTable table={gruposTable} />
              </TabPanel>
              <TabPanel value={evaSubTab} index={3}>
                <MaterialReactTable table={modulosTable} />
              </TabPanel>
              <TabPanel value={evaSubTab} index={4}>
                <MaterialReactTable table={permisosTable} />
              </TabPanel>
              <TabPanel value={evaSubTab} index={5}>
                <MaterialReactTable table={empresasTable} />
              </TabPanel>
              <TabPanel value={evaSubTab} index={6}>
                <MaterialReactTable table={areasTable} />
              </TabPanel>
              <TabPanel value={evaSubTab} index={7}>
                <MaterialReactTable table={departamentosTable} />
              </TabPanel>
              <TabPanel value={evaSubTab} index={8}>
                <MaterialReactTable table={canalesTable} />
              </TabPanel>
              <TabPanel value={evaSubTab} index={9}>
                <MaterialReactTable table={userGroupsTable} />
              </TabPanel>
              <TabPanel value={evaSubTab} index={10}>
                <MaterialReactTable table={userPermsTable} />
              </TabPanel>
              <TabPanel value={evaSubTab} index={11}>
                <MaterialReactTable table={groupPermsTable} />
              </TabPanel>
            </>
          )}
        </>
      )}

      {/* ============ SOFTNET SECTION ============================== */}
      {mainTab === 1 && (
        <>
          <Paper sx={{ mb: 2 }}>
            <Tabs
              value={softnetSubTab}
              onChange={(_, v) => setSoftnetSubTab(v)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab icon={<Insights />} iconPosition="start" label="Dashboard" />
              <Tab icon={<People />} iconPosition="start" label={`Usuarios (${softnetData?.usuarios.length ?? 0})`} />
              <Tab icon={<Layers />} iconPosition="start" label={`Módulos (${softnetData?.modulos?.length ?? 0})`} />
              <Tab icon={<Domain />} iconPosition="start" label={`Departamentos (${softnetData?.departamentos?.length ?? 0})`} />
              <Tab icon={<Badge />} iconPosition="start" label={`Cargos (${softnetData?.cargos?.length ?? 0})`} />
              <Tab icon={<Link />} iconPosition="start" label={`Usuario→Cargo (${softnetUserCargoPairs.length})`} />
              <Tab icon={<Link />} iconPosition="start" label={`Usuario→Depto (${softnetUserDeptoPairs.length})`} />
            </Tabs>
          </Paper>

          <TabPanel value={softnetSubTab} index={0}>
            {softnetLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                <CircularProgress />
              </Box>
            ) : softnetError || !softnetData ? (
              <Alert severity="info">
                No se pudieron cargar los datos de SOFTNET. Verifique la conexión con el servicio.
              </Alert>
            ) : (
              <>
              <Grid container spacing={2} mb={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <KpiCard label="Usuarios SOFTNET" value={softnetData.usuarios.length} icon={<People sx={{ fontSize: 40 }} />} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <KpiCard
                    label="Activos"
                    value={softnetData.usuarios.filter((u) => u.estado === 'activo').length}
                    hint={`${softnetData.usuarios.filter((u) => u.estado !== 'activo').length} inactivos`}
                    icon={<VerifiedUser sx={{ fontSize: 40 }} />}
                    color="success.main"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <KpiCard label="Módulos" value={softnetData.modulos?.length ?? 0} icon={<Layers sx={{ fontSize: 40 }} />} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <KpiCard label="Departamentos" value={softnetData.departamentos?.length ?? 0} icon={<Domain sx={{ fontSize: 40 }} />} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <KpiCard label="Cargos" value={softnetData.cargos?.length ?? 0} icon={<Badge sx={{ fontSize: 40 }} />} />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Usuarios por Departamento</Typography>
                    <ResponsiveContainer
                      width="100%"
                      height={Math.max(320, softnetUsersByDept.length * 30)}
                    >
                      <BarChart data={softnetUsersByDept} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" allowDecimals={false} />
                        <YAxis type="category" dataKey="label" width={180} tick={{ fontSize: 12 }} interval={0} />
                        <ReTooltip />
                        <Bar dataKey="value" name="Usuarios" radius={[0, 4, 4, 0]}>
                          {softnetUsersByDept.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Usuarios por Área</Typography>
                    <ResponsiveContainer
                      width="100%"
                      height={Math.max(320, softnetUsersByArea.length * 30)}
                    >
                      <BarChart data={softnetUsersByArea} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" allowDecimals={false} />
                        <YAxis type="category" dataKey="label" width={180} tick={{ fontSize: 12 }} interval={0} />
                        <ReTooltip />
                        <Bar dataKey="value" name="Usuarios" radius={[0, 4, 4, 0]}>
                          {softnetUsersByArea.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[(i + 3) % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Usuarios por Estado</Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie data={softnetUserEstadoPie} dataKey="value" nameKey="label" cx="50%" cy="50%" outerRadius={90} label={({ label, value }) => `${label}: ${value}`}>
                          {softnetUserEstadoPie.map((_, i) => (
                            <Cell key={i} fill={i === 0 ? '#4caf50' : '#f44336'} />
                          ))}
                        </Pie>
                        <ReTooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Módulos por Tipo</Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={softnetModulosByType}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" />
                        <YAxis allowDecimals={false} />
                        <ReTooltip />
                        <Bar dataKey="value" fill={CHART_COLORS[4]} name="Módulos" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Usuarios por Perfil</Typography>
                    <ResponsiveContainer
                      width="100%"
                      height={Math.max(320, softnetUsersByProfile.length * 30)}
                    >
                      <BarChart data={softnetUsersByProfile} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" allowDecimals={false} />
                        <YAxis type="category" dataKey="label" width={220} tick={{ fontSize: 12 }} interval={0} />
                        <ReTooltip />
                        <Bar dataKey="value" name="Usuarios" radius={[0, 4, 4, 0]}>
                          {softnetUsersByProfile.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[(i + 2) % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
              </Grid>
              </>
            )}
          </TabPanel>

          <TabPanel value={softnetSubTab} index={1}>
            {softnetLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                <CircularProgress />
              </Box>
            ) : softnetError || !softnetData ? (
              <Alert severity="info">
                No se pudieron cargar los datos de SOFTNET. Verifique la conexión con el servicio.
              </Alert>
            ) : (
              <MaterialReactTable table={softnetUsuariosTable} />
            )}
          </TabPanel>

          <TabPanel value={softnetSubTab} index={2}>
            {softnetLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                <CircularProgress />
              </Box>
            ) : softnetError || !softnetData ? (
              <Alert severity="info">
                No se pudieron cargar los datos de SOFTNET. Verifique la conexión con el servicio.
              </Alert>
            ) : (
              <MaterialReactTable table={softnetModulosTable} />
            )}
          </TabPanel>

          <TabPanel value={softnetSubTab} index={3}>
            {softnetLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                <CircularProgress />
              </Box>
            ) : softnetError || !softnetData ? (
              <Alert severity="info">
                No se pudieron cargar los datos de SOFTNET. Verifique la conexión con el servicio.
              </Alert>
            ) : (
              <MaterialReactTable table={softnetDeptosTable} />
            )}
          </TabPanel>

          <TabPanel value={softnetSubTab} index={4}>
            {softnetLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                <CircularProgress />
              </Box>
            ) : softnetError || !softnetData ? (
              <Alert severity="info">
                No se pudieron cargar los datos de SOFTNET. Verifique la conexión con el servicio.
              </Alert>
            ) : (
              <MaterialReactTable table={softnetCargosTable} />
            )}
          </TabPanel>

          <TabPanel value={softnetSubTab} index={5}>
            {softnetLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                <CircularProgress />
              </Box>
            ) : softnetError || !softnetData ? (
              <Alert severity="info">
                No se pudieron cargar los datos de SOFTNET. Verifique la conexión con el servicio.
              </Alert>
            ) : (
              <MaterialReactTable table={userCargoTable} />
            )}
          </TabPanel>

          <TabPanel value={softnetSubTab} index={6}>
            {softnetLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                <CircularProgress />
              </Box>
            ) : softnetError || !softnetData ? (
              <Alert severity="info">
                No se pudieron cargar los datos de SOFTNET. Verifique la conexión con el servicio.
              </Alert>
            ) : (
              <MaterialReactTable table={userDeptoTable} />
            )}
          </TabPanel>
        </>
      )}
    </Box>
  );
};

export default ReporteUsuariosDashboardPage;
